import { Stack, Link } from "expo-router";
import { BookOpen, Lightbulb, MessageSquareText, School, ShieldCheck } from "lucide-react-native";
import { ScrollView, StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

const features = [
  {
    icon: Lightbulb,
    title: "日常の発見を記録",
    body: "身近な疑問や気づきを、学びの投稿として残せます。",
  },
  {
    icon: BookOpen,
    title: "学問カードで深める",
    body: "国語や思考法などのカードを使い、見方や考え方を広げます。",
  },
  {
    icon: School,
    title: "授業と振り返りに活用",
    body: "探究活動のメモや学習ポートフォリオづくりに使えます。",
  },
];

export default function MarketingPage() {
  return (
    <>
      <Stack.Screen options={{ title: "学問レンズ" }} />
      <ScrollView style={styles.page} contentContainerStyle={styles.content}>
        <View style={styles.hero}>
          <View style={styles.badge}>
            <ShieldCheck size={16} color="#116466" />
            <Text style={styles.badgeText}>教育向け学習アプリ</Text>
          </View>
          <Text style={styles.kicker}>学問レンズ</Text>
          <Text style={styles.title}>日常の発見を、学びに変える。</Text>
          <Text style={styles.lead}>
            学問レンズは、身近な出来事や疑問を記録し、学問カードの視点で考えを深めるためのアプリです。
          </Text>
          <View style={styles.actions}>
            <Link href={"/privacy" as never} style={styles.primaryLink}>
              プライバシーポリシー
            </Link>
            <Link href="/" style={styles.secondaryLink}>
              アプリを開く
            </Link>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>できること</Text>
          <View style={styles.featureGrid}>
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <View key={feature.title} style={styles.featureCard}>
                  <View style={styles.iconCircle}>
                    <Icon size={24} color="#116466" />
                  </View>
                  <Text style={styles.featureTitle}>{feature.title}</Text>
                  <Text style={styles.featureBody}>{feature.body}</Text>
                </View>
              );
            })}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>利用シーン</Text>
          <View style={styles.useCase}>
            <MessageSquareText size={24} color="#2f3e46" />
            <View style={styles.useCaseText}>
              <Text style={styles.useCaseTitle}>授業、家庭学習、振り返りに</Text>
              <Text style={styles.paragraph}>
                教科の知識を暗記するだけでなく、「なぜそう見えるのか」「どの考え方で説明できるのか」を自分の言葉で残せます。
                投稿ごとに公開範囲を選びながら、発見・写真・振り返りを整理できます。
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerText}>© 2026 Upsee Inc. 学問レンズ</Text>
        </View>
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: "#f7faf8",
  },
  content: {
    alignItems: "center",
    paddingBottom: 40,
  },
  hero: {
    width: "100%",
    maxWidth: 980,
    paddingHorizontal: 24,
    paddingTop: 72,
    paddingBottom: 48,
  },
  badge: {
    alignSelf: "flex-start",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    borderWidth: 1,
    borderColor: "#b7d8d2",
    backgroundColor: "#edf8f5",
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 8,
    marginBottom: 24,
  },
  badgeText: {
    color: "#116466",
    fontWeight: "700",
  },
  kicker: {
    color: "#116466",
    fontSize: 18,
    fontWeight: "700",
    marginBottom: 10,
  },
  title: {
    color: "#1f2933",
    fontSize: 44,
    lineHeight: 56,
    fontWeight: "800",
    marginBottom: 18,
  },
  lead: {
    color: "#4b5563",
    fontSize: 18,
    lineHeight: 30,
    maxWidth: 760,
  },
  actions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 28,
  },
  primaryLink: {
    backgroundColor: "#116466",
    color: "#fff",
    fontWeight: "700",
    paddingHorizontal: 18,
    paddingVertical: 12,
    borderRadius: 8,
    overflow: "hidden",
  },
  secondaryLink: {
    borderWidth: 1,
    borderColor: "#b9c4ce",
    color: "#2f3e46",
    fontWeight: "700",
    paddingHorizontal: 18,
    paddingVertical: 12,
    borderRadius: 8,
    overflow: "hidden",
  },
  section: {
    width: "100%",
    maxWidth: 980,
    paddingHorizontal: 24,
    paddingVertical: 28,
  },
  sectionTitle: {
    color: "#1f2933",
    fontSize: 26,
    fontWeight: "800",
    marginBottom: 18,
  },
  featureGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 14,
  },
  featureCard: {
    flexGrow: 1,
    flexBasis: 240,
    borderWidth: 1,
    borderColor: "#d8e1e8",
    backgroundColor: "#fff",
    borderRadius: 8,
    padding: 18,
  },
  iconCircle: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#e8f5f2",
    marginBottom: 14,
  },
  featureTitle: {
    color: "#1f2933",
    fontSize: 18,
    fontWeight: "800",
    marginBottom: 8,
  },
  featureBody: {
    color: "#5f6b76",
    fontSize: 15,
    lineHeight: 24,
  },
  useCase: {
    flexDirection: "row",
    gap: 14,
    borderLeftWidth: 4,
    borderLeftColor: "#f2c94c",
    backgroundColor: "#fff",
    padding: 18,
  },
  useCaseText: {
    flex: 1,
  },
  useCaseTitle: {
    color: "#1f2933",
    fontSize: 18,
    fontWeight: "800",
    marginBottom: 8,
  },
  paragraph: {
    color: "#4b5563",
    fontSize: 16,
    lineHeight: 28,
  },
  footer: {
    width: "100%",
    maxWidth: 980,
    paddingHorizontal: 24,
    paddingTop: 20,
  },
  footerText: {
    color: "#6b7280",
    fontSize: 13,
  },
});
